import { useState, useCallback, useEffect, useRef } from 'react';

// Prefix for the in-tab broadcast events. The native `storage` event only
// fires in *other* tabs, so we dispatch our own event to keep multiple hook
// instances (and the achievement-sync layer) in sync within the same tab.
const EVENT_PREFIX = 'local-storage';

export function eventNameFor(key: string): string {
  return `${EVENT_PREFIX}:${key}`;
}

// Write a value to localStorage and notify all listeners in this tab. Exposed
// so non-hook code (e.g. server-sync reconciliation) can update a key and have
// every mounted useLocalStorage instance pick it up immediately.
export function writeLocalStorage<T>(key: string, value: T): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    window.dispatchEvent(new CustomEvent(eventNameFor(key), { detail: value }));
  } catch {
    // quota exceeded — silently ignore
  }
}

export function useLocalStorage<T>(key: string, defaultValue: T): [T, (value: T | ((prev: T) => T)) => void] {
  // Keep the latest default in a ref so the sync effect can reference it
  // without resubscribing when callers pass a fresh literal each render.
  const defaultRef = useRef(defaultValue);
  defaultRef.current = defaultValue;

  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? (JSON.parse(item) as T) : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      setStoredValue((prev) => {
        const next = value instanceof Function ? value(prev) : value;
        writeLocalStorage(key, next);
        return next;
      });
    },
    [key],
  );

  // Stay in sync with writes from other hook instances (same tab, via our
  // CustomEvent) and other tabs (via the native storage event).
  useEffect(() => {
    const onCustom = (e: Event) => {
      setStoredValue((e as CustomEvent<T>).detail);
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key !== key) return;
      try {
        setStoredValue(e.newValue ? (JSON.parse(e.newValue) as T) : defaultRef.current);
      } catch {
        setStoredValue(defaultRef.current);
      }
    };
    window.addEventListener(eventNameFor(key), onCustom);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(eventNameFor(key), onCustom);
      window.removeEventListener('storage', onStorage);
    };
  }, [key]);

  return [storedValue, setValue];
}
